import { createFileRoute } from "@tanstack/react-router"
import { ArrowLeft, Building, ChevronDown, Eye, FileText, Folder, Users } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import JournalOrganization, {
  JournalPriorityBadge,
} from "@/components/forms/summary/organize-journal"
import {
  ResourceDetailModal,
  type ResourceDisplay,
} from "@/components/modals/resource-detail-modal"
import { Page } from "@/components/page"
import { PageLoader } from "@/components/page-loader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Spinner } from "@/components/ui/custom/spinner"
import { Typography } from "@/components/ui/typography"
import { trpc } from "@/lib/trpc"
import { cn } from "@/lib/utils"

export const Route = createFileRoute("/_authed/_individual/personal/journal-management")({
  component: JournalManagementPage,
})

function JournalManagementPage() {
  const { data: user, isLoading: userLoading } = trpc.users.me.useQuery()
  const estateId = user?.estateId ?? ""

  if (userLoading) {
    return (
      <Page
        headerSlot={
          <Typography as="h1" size="2xl">
            Journal Management
          </Typography>
        }
      >
        <PageLoader />
      </Page>
    )
  }

  return (
    <Page
      headerSlot={
        <Typography as="h1" size="2xl">
          Journal Management
        </Typography>
      }
    >
      <div className="space-y-8">
        {/* My Journals */}
        <JournalOrganization estateId={estateId} />
        {/* Shared Journals */}
        <SharedJournals />
      </div>
    </Page>
  )
}

function SharedJournals() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [selectedJournalId, setSelectedJournalId] = useState<string | null>(null)

  const { data: sharedJournals = [], isLoading } = trpc.journals.listMySharedJournals.useQuery()

  const { data: journalResources, isLoading: resourcesLoading } =
    trpc.journals.listResource.useQuery(
      { journalId: selectedJournalId ?? "" },
      { enabled: !!selectedJournalId },
    )

  const selectedJournal = sharedJournals.find((j) => j.id === selectedJournalId)

  if (selectedJournalId && selectedJournal) {
    return (
      <Card>
        <CardContent className="space-y-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedJournalId(null)}
            className="-ml-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to shared journals
          </Button>

          <div className="flex items-start gap-3">
            <div className="p-3 bg-primary/10 rounded-lg shrink-0">
              <Folder className="w-6 h-6 text-primary" />
            </div>
            <div className="min-w-0">
              <Typography as="h2" size="xl">
                {selectedJournal.name}
              </Typography>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Typography as="span" size="sm" className="text-muted-foreground">
                  {t("components.journal.enums.journalType.option", {
                    context: selectedJournal.journalType,
                  })}
                </Typography>
                <JournalPriorityBadge priority={selectedJournal.journalPriority} />
              </div>
              {selectedJournal.estateName && (
                <Typography as="p" size="sm" className="text-muted-foreground mt-1">
                  Estate: {selectedJournal.estateName}
                </Typography>
              )}
            </div>
          </div>

          {/* Read-only Resources */}
          <div>
            <Typography as="h3" size="lg">
              Journal resources
            </Typography>
            <Typography as="p" size="sm" className="text-muted-foreground mb-4">
              Resources linked to this journal ({journalResources?.length ?? 0} items)
            </Typography>
            <ReadOnlyResourceList resources={journalResources} isLoading={resourcesLoading} />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className="py-0">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between gap-4 px-6 py-4 text-left"
          >
            <span className="flex items-center gap-2 min-w-0">
              <Users className="w-4 h-4 text-muted-foreground shrink-0" />
              <Typography as="h2" size="lg" className="truncate">
                Shared journals
              </Typography>
            </span>
            <span className="flex items-center gap-3 shrink-0">
              <Badge variant="secondary">{sharedJournals.length} · view-only</Badge>
              <ChevronDown
                className={cn(
                  "w-4 h-4 text-muted-foreground transition-transform",
                  open && "rotate-180",
                )}
              />
            </span>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 pb-6">
            <Typography as="p" size="sm" className="text-muted-foreground mb-4">
              Journals shared with you by other users. These are view-only.
            </Typography>

            {isLoading && <PageLoader />}

            {!isLoading && sharedJournals.length === 0 && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Folder className="w-8 h-8 text-muted-foreground" />
                </div>
                <Typography as="h3" size="lg" className="mb-2">
                  No shared journals yet
                </Typography>
                <Typography as="p" className="text-muted-foreground">
                  When someone shares a journal with you, it will appear here.
                </Typography>
              </div>
            )}

            {!isLoading && sharedJournals.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sharedJournals.map((journal) => (
                  <Card
                    key={journal.id}
                    className="hover:shadow-md transition-shadow flex flex-col justify-between"
                  >
                    <CardContent className="p-4 flex flex-col justify-between flex-1 gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-primary/10 rounded flex items-center justify-center shrink-0">
                            <Folder className="w-5 h-5 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-card-foreground truncate">
                              {journal.name}
                            </h3>
                            <p className="text-sm text-muted-foreground truncate">
                              {t("components.journal.enums.journalType.option", {
                                context: journal.journalType,
                              })}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          <JournalPriorityBadge priority={journal.journalPriority} />
                          {journal.estateName && (
                            <Typography as="span" size="sm" className="text-muted-foreground">
                              {journal.estateName}
                            </Typography>
                          )}
                        </div>
                      </div>

                      <Button
                        variant="outline"
                        className="w-full"
                        size="sm"
                        onClick={() => setSelectedJournalId(journal.id)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}

function getResourceDisplayInfo(resource: ResourceDisplay) {
  switch (resource.type) {
    case "contact":
      return resource.email || resource.phoneNumber || "Contact"
    case "asset":
      return resource.currentValue ? `$${resource.currentValue}` : "Asset"
    case "document":
      return resource.description || "Document"
    case "company":
      return resource.companyDetails || "Company"
    case "task":
      return resource.dueDate ? `Due: ${new Date(resource.dueDate).toLocaleDateString()}` : "Task"
    default:
      return ""
  }
}

function getResourceDisplayName(resource: ResourceDisplay) {
  if (resource.type === "asset") return resource.name
  if (resource.type === "contact")
    return `${resource.firstName ?? ""} ${resource.lastName ?? ""}`.trim()
  if (resource.type === "company") return resource.companyName || resource.name
  return resource.title || resource.name
}

function getResourceIcon(type: string) {
  switch (type) {
    case "contact":
      return <Users className="w-4 h-4 text-primary" />
    case "asset":
      return <Building className="w-4 h-4 text-primary" />
    case "document":
      return <FileText className="w-4 h-4 text-primary" />
    default:
      return <Folder className="w-4 h-4 text-primary" />
  }
}

function ReadOnlyResourceList({
  resources,
  isLoading,
}: {
  resources: unknown[] | undefined
  isLoading: boolean
}) {
  const [selectedResource, setSelectedResource] = useState<ResourceDisplay | null>(null)
  const [detailModalOpen, setDetailModalOpen] = useState(false)

  if (isLoading) {
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-center">
        <Spinner variant="default" size={32} />
        <Typography as="p" className="text-muted-foreground">
          Loading resources...
        </Typography>
      </div>
    )
  }

  const validResources = (resources ?? []).filter(
    (r): r is ResourceDisplay => r != null && typeof r === "object" && "type" in r,
  )

  if (validResources.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <Folder className="w-8 h-8 text-muted-foreground" />
        </div>
        <Typography as="h3" size="lg" className="mb-2">
          No resources linked
        </Typography>
        <Typography as="p" className="text-muted-foreground">
          This journal has no linked resources.
        </Typography>
      </div>
    )
  }

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {validResources.map((resource) => (
          <Card
            key={resource.journalResourceId}
            className="p-4 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => {
              setSelectedResource(resource)
              setDetailModalOpen(true)
            }}
          >
            <div className="flex items-start gap-3">
              <div className="p-2 bg-primary/10 rounded">{getResourceIcon(resource.type)}</div>
              <div className="flex-1 min-w-0">
                <Typography as="h3" className="font-medium truncate">
                  {getResourceDisplayName(resource)}
                </Typography>
                <Typography as="p" className="text-sm text-muted-foreground capitalize">
                  {resource.type}
                </Typography>
                <Typography as="p" className="text-xs text-muted-foreground mt-1">
                  {getResourceDisplayInfo(resource)}
                </Typography>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <ResourceDetailModal
        open={detailModalOpen}
        setOpen={setDetailModalOpen}
        resource={selectedResource}
      />
    </>
  )
}
